import {
  AfterViewInit,
  Component,
  ElementRef,
  NgZone,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { TvFeature } from 'src/app/data/enum/tv-feature';
import gsap from 'gsap';
import { LocalizationService } from 'src/app/service/localization.service';
import { RemoteControlService } from 'src/app/service/remote-control.service';
import createInitialPageTimeline from '../../utils/animation/initial-page-timeline';
import endPageTimeline from '../../utils/animation/end-page-timeline';
import FeaturePage from 'src/app/core/feature-page';
import { AnalyticsEvent, triggerEvent } from 'src/app/utils/analytics';
import { AnimationOptions } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';
import { JsonKey } from 'src/app/data/type/locale-copy-key';
import { environment } from 'src/environments/environment';
import { ParameterService } from 'src/app/service/parameter.service';

@Component({
  selector: 'app-watchlist-page',
  templateUrl: './watchlist-page.component.html',
  styleUrls: ['./watchlist-page.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class WatchlistPageComponent extends FeaturePage implements OnInit, AfterViewInit {
  @ViewChild('watchlistRef') containerElementRef: ElementRef;

  public lottieOptions: AnimationOptions = {
    path: `${environment.baseHref}/assets/animation/watchlist-device-lottie.json`,
    autoplay: false,
    loop: false,
  };

  public copy: string;

  private animationItem: AnimationItem;
  private watchlistTimeline: GSAPTimeline = gsap.timeline({ paused: true });

  constructor(
    protected localizationService: LocalizationService,
    protected remoteControlService: RemoteControlService,
    protected parameterService: ParameterService,
    protected zone: NgZone,
  ) {
    super(localizationService, remoteControlService, parameterService, zone, TvFeature.WATCHLIST);
  }

  ngOnInit(): void {
    this.copy = this.localizationService.translate(JsonKey.WATCHLIST_ADD_SHOWS_FROM_DEVICE);
  }

  handleLastStep() {
    gsap.delayedCall(1.5, () => endPageTimeline(this.containerElementRef).play()),
      triggerEvent(AnalyticsEvent.watchlistComplete);
  }

  handleRemoteSignal() {
    const nextStepIndex = this.stepIndex + 1;

    if (nextStepIndex === 1) {
      this.isRemoteDisabled = true;
      triggerEvent(AnalyticsEvent.watchlistFirstInteraction);

      gsap.set('.tooltip-position-wrapper', { autoAlpha: 0 });
      this.watchlistTimeline.play().then(() => {
        this.playLottie();
      });
    }
  }

  crateWatchlistTimeline(): GSAPTimeline {
    return gsap
      .timeline()
      .to('[data-transition="1"]', { autoAlpha: 0 })
      .to('[data-transition="2"]', { autoAlpha: 0 }, '+=1')
      .to('.legal-quote', { autoAlpha: 0 }, '-=1')
      .fromTo(
        '[data-animation="devices-screen-wrapper"]',
        { autoAlpha: 0 },
        { autoAlpha: 1 },
        '+=1',
      )
      .fromTo(
        '[data-animation="devices-title"]',
        { autoAlpha: 0, y: 30 },
        { autoAlpha: 1, y: 0, yoyo: true, repeat: 1, repeatDelay: 3 },
      )
      .to('[data-animation="devices-overlay"]', { autoAlpha: 0.3 })
      .fromTo('.lottie-wrapper', { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.1 }, '-=1');
  }

  playLottie(): void {
    this.zone.runOutsideAngular(() => {
      this.animationItem.play();
    });
  }

  onLottieEnd(): void {
    gsap
      .timeline()
      .to('[data-animation="devices-screen-wrapper"]', { autoAlpha: 0 }, 0)
      .to('[data-transition="3"]', { autoAlpha: 0 }, 0)
      .then(this.nextStep.bind(this));
  }

  animationCreated(animationItem: AnimationItem): void {
    this.animationItem = animationItem;
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    createInitialPageTimeline(this.containerElementRef, this.isAbstract).play();
    this.watchlistTimeline.add(this.crateWatchlistTimeline());
  }
}
